import { AlertCircle, Ban, FileQuestion, RefreshCw, ServerCrash } from 'lucide-react'
import { Button } from '@/components/ui/button'
import type { ReactNode } from 'react'

interface ErrorDisplayProps {
  /** HTTP status code or error type */
  status?: number | 'error' | 'network'
  /** Custom title (overrides default for status) */
  title?: string
  /** Custom message (overrides default for status) */
  message?: string
  /** Retry callback - shows retry button when provided */
  onRetry?: () => void
  /** Whether retry is in progress */
  isRetrying?: boolean
  /** Additional actions to show */
  actions?: ReactNode
}

interface ErrorConfig {
  icon: ReactNode
  title: string
  message: string
  showRetry: boolean
}

function getErrorConfig(status?: number | 'error' | 'network'): ErrorConfig {
  const iconClass = 'h-12 w-12'

  switch (status) {
    case 403:
      return {
        icon: <Ban className={`${iconClass} text-destructive`} />,
        title: 'Access Denied',
        message: "You don't have permission to view this page. Contact your administrator if you believe this is an error.",
        showRetry: false,
      }
    case 404:
      return {
        icon: <FileQuestion className={`${iconClass} text-muted-foreground`} />,
        title: 'Not Found',
        message: "The page or resource you're looking for doesn't exist or has been moved.",
        showRetry: false,
      }
    case 500:
    case 502:
    case 503:
    case 504:
      return {
        icon: <ServerCrash className={`${iconClass} text-destructive`} />,
        title: 'Server Error',
        message: 'Something went wrong on our end. Please try again later.',
        showRetry: true,
      }
    case 'network':
      return {
        icon: <AlertCircle className={`${iconClass} text-destructive`} />,
        title: 'Network Error',
        message: 'Unable to connect. Please check your internet connection and try again.',
        showRetry: true,
      }
    default:
      return {
        icon: <AlertCircle className={`${iconClass} text-destructive`} />,
        title: 'Something went wrong',
        message: 'An unexpected error occurred. Please try again.',
        showRetry: true,
      }
  }
}

/**
 * Display component for error states (403, 404, 500, network errors).
 */
export function ErrorDisplay({
  status,
  title,
  message,
  onRetry,
  isRetrying = false,
  actions,
}: ErrorDisplayProps) {
  const config = getErrorConfig(status)

  return (
    <div className="flex min-h-[400px] flex-col items-center justify-center p-8 text-center">
      <div className="mb-4">{config.icon}</div>
      <h2 className="text-2xl font-bold text-foreground">
        {title ?? config.title}
      </h2>
      <p className="mt-2 max-w-md text-muted-foreground">
        {message ?? config.message}
      </p>
      <div className="mt-6 flex items-center gap-3">
        {config.showRetry && onRetry && (
          <Button onClick={onRetry} disabled={isRetrying} variant="outline">
            <RefreshCw className={`mr-2 h-4 w-4 ${isRetrying ? 'animate-spin' : ''}`} />
            {isRetrying ? 'Retrying...' : 'Try again'}
          </Button>
        )}
        {actions}
      </div>
    </div>
  )
}

/**
 * Full-page 403 Forbidden error.
 */
export function Forbidden({ onBack }: { onBack?: () => void }) {
  return (
    <ErrorDisplay
      status={403}
      actions={
        onBack && (
          <Button variant="default" onClick={onBack}>
            Go back
          </Button>
        )
      }
    />
  )
}

/**
 * Full-page 404 Not Found error.
 */
export function NotFound({ onBack }: { onBack?: () => void }) {
  return (
    <ErrorDisplay
      status={404}
      actions={
        onBack && (
          <Button variant="default" onClick={onBack}>
            Go back
          </Button>
        )
      }
    />
  )
}

